import React, { useCallback, useContext } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled';
import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InformationIcon from '@atlaskit/icon/core/information-circle';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { useContentType, usePageSpaceKey } from '@confluence/page-context';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { SIDEBAR_DETAILS_PANEL_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useCanEditLabels } from '@confluence/details-panel/entry-points/useCanEditLabels.ts';
import { preloadRedactionsQueries } from '@confluence/details-panel/entry-points/preloadRedactionsQueries.ts';
import { useShowDetailsPanel } from '@confluence/details-panel/entry-points/useShowDetailsPanel.ts';
import {
	useObjectSidebarActions,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { EditLabelDialogLoader } from '@confluence/labels/entry-points/EditLabelDialogLoader.ts';
import { ATTACHMENTS_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	ShortcutVisualizer,
	isMac,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT,
	LABELS_SHORTCUT,
	UNIVERSAL_LABELS_SHORTCUT,
	VIEW_ATTACHMENTS_SHORTCUT,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { expVal } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useNoEditAccessFlag } from '@confluence/edit-features/entry-points/useNoEditAccessFlag.ts';

import { OBJECT_SIDEBAR_TOUR_TARGETS } from '../objectSidebarTourTargets.ts';

const i18n = defineMessages({
	openDetailsPanelLabel: {
		id: 'object-sidebar-components.open.details.panel.label',
		defaultMessage: 'Open Details Panel',
		description: 'Label for button to open the details panel',
	},
	showDetailsLabel: {
		id: 'object-sidebar-components.show.details.label',
		description:
			"The text is shown in a tooltip when the Object Sidebar's Details button is hovered.",
		defaultMessage: 'Show details',
	},
});

export const DetailsPanelButton = withFlags(
	({
		contentId,
		collapsed,
		isDisabled = false,
		flags,
	}: {
		contentId: string;
		collapsed?: boolean;
		isDisabled?: boolean;
		flags: FlagsStateContainer;
	}) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { showDetailsPanel } = useShowDetailsPanel();
		const [contentTypeFromState] = useContentType();
		// keyboard events do not bubble up to confluence when focus is in the iframe.
		// Since the keyboard shortcut does not work, we must hide the shortcut text.
		const isShortcutTextShown = contentTypeFromState !== ContentType.DATABASE;
		const panel = usePanel();
		const { hideObjectSidebar } = useObjectSidebarActions();
		const [spaceKey = ''] = usePageSpaceKey();
		const { showModal } = useDialogs();
		const { isLabelsAvailable, canEditLabels } = useCanEditLabels();
		const { push } = useRouteActions();
		const isRovoButtonEnabled = useAIRovoButtonEnablement();
		const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
		const isOnEditRoute = useIsEditorPage();
		const pageMode = useGetPageMode();
		const { showNoEditAccessFlag } = useNoEditAccessFlag(flags);

		const openDetailsPanelShortcutStrings = useShortcutStrings({
			shortcut: UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT,
		});

		const [canUpdateContent] = useCanPerformContentOperation({
			contentId: contentId ?? '',
			contentType: contentTypeFromState,
			operation: OperationTypes.UPDATE,
		});

		const handleClickDetailsButton = useCallback(() => {
			experienceTracker.start({
				name: SIDEBAR_DETAILS_PANEL_LOAD_EXPERIENCE,
			});
			showDetailsPanel();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'detailsPanel',
					source: 'contentWrapper',
					attributes: {
						contentType: contentTypeFromState,
						pageMode,
					},
				},
			}).fire();
		}, [showDetailsPanel, createAnalyticsEvent, experienceTracker, contentTypeFromState, pageMode]);

		// Avoids CLS from redactions feature entitlement query in DetailsPanel
		const onHoverDetailsButton = useCallback(() => {
			if (fg('dlp_cc-redactions-metadata-modal')) {
				void preloadRedactionsQueries({
					contentId,
					canUpdateContent: !!canUpdateContent,
				});
			}
		}, [contentId, canUpdateContent]);

		const toggleDetailsPanel = useCallback(
			(e: KeyboardEvent) => {
				e.preventDefault();
				if (panel?.id === 'detailsPanel') {
					void hideObjectSidebar();
				} else {
					handleClickDetailsButton();
				}
			},
			[handleClickDetailsButton, hideObjectSidebar, panel?.id],
		);

		const openLabelsDialog = useCallback(() => {
			showModal(EditLabelDialogLoader, {
				contentId,
				spaceKey,
				source: 'detailsPanelKeyboardShortcut',
			});
		}, [showModal, contentId, spaceKey]);

		const shortcutListeners = (
			<>
				<GeneralShortcutListener
					accelerator={UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT}
					listener={toggleDetailsPanel}
				/>
				{isLabelsAvailable && (
					<GeneralShortcutListener
						deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? LABELS_SHORTCUT : undefined}
						accelerator={UNIVERSAL_LABELS_SHORTCUT}
						listener={canEditLabels ? openLabelsDialog : showNoEditAccessFlag}
					/>
				)}
				<GeneralShortcutListener
					deprecatedAccelerator={
						!(isLiveEditMode || isOnEditRoute) ? VIEW_ATTACHMENTS_SHORTCUT : undefined
					}
					accelerator={
						isMac()
							? UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC
							: UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN
					}
					listener={() =>
						push(
							expVal('cc_attachments_page', 'isEnabled', false)
								? ATTACHMENTS_PAGE.toUrl({ spaceKey, contentId })
								: `${CONTEXT_PATH}/pages/viewpageattachments.action?pageId=${contentId}`,
						)
					}
				/>
			</>
		);

		if (collapsed) {
			return shortcutListeners;
		}

		return (
			<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.SIDEBAR_DETAILS_BUTTON}>
				<Box
					onMouseEnter={onHoverDetailsButton}
					data-vc="object-sidebar-details-button"
					testId="object-sidebar-details-button-id"
				>
					<Tooltip
						position="left"
						content={
							isShortcutTextShown && !fg('platform_tooltip_shortcuts_migration_1') ? (
								<ShortcutVisualizer
									shortcut={UNIVERSAL_OPEN_DETAILS_PANEL_SHORTCUT}
									contentBefore={formatMessage(i18n.showDetailsLabel)}
									isTooltip
								/>
							) : (
								<FormattedMessage {...i18n.showDetailsLabel} />
							)
						}
						shortcut={
							isShortcutTextShown && fg('platform_tooltip_shortcuts_migration_1')
								? openDetailsPanelShortcutStrings
								: undefined
						}
						delay={0}
						hideTooltipOnClick
					>
						<IconButton
							onClick={handleClickDetailsButton}
							appearance="subtle"
							shape={isRovoButtonEnabled ? 'default' : 'circle'}
							icon={InformationIcon}
							label={formatMessage(i18n.openDetailsPanelLabel)}
							testId="sidebar-details-button"
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
							interactionName="details-panel-interaction"
						/>
						{shortcutListeners}
					</Tooltip>
				</Box>
			</SpotlightTarget>
		);
	},
);
